export const DEFAULT_COMMISSION_BILL_TO_PRINT_OPTIONS = {
  showEmail: true,
  showPhone: true,
  showCountry: true,
  showCampus: true,
  showBillingAddress: true,
};

export function normalizeCommissionBillToPrintOptions(raw) {
  if (!raw || typeof raw !== "object") {
    return { ...DEFAULT_COMMISSION_BILL_TO_PRINT_OPTIONS };
  }
  return {
    showEmail: raw.showEmail !== false,
    showPhone: raw.showPhone !== false,
    showCountry: raw.showCountry !== false,
    showCampus: raw.showCampus !== false,
    showBillingAddress: raw.showBillingAddress !== false,
  };
}

export function readBillToPrintOptionsFromSnapshot(snapshot) {
  if (!snapshot || typeof snapshot !== "object") {
    return { ...DEFAULT_COMMISSION_BILL_TO_PRINT_OPTIONS };
  }
  return normalizeCommissionBillToPrintOptions(snapshot.bill_to_print_options);
}

/** Prefer live provider fields; keep snapshot address when provider has none yet. */
export function mergeProviderBillToForInvoice(existing, live) {
  const prev = existing && typeof existing === "object" ? existing : {};
  const next = live && typeof live === "object" ? live : {};
  return {
    ...prev,
    ...next,
    id: next.id ?? prev.id ?? null,
    fullName: String(next.fullName || prev.fullName || "").trim(),
    email: String(next.email || prev.email || "").trim(),
    phone: String(next.phone || prev.phone || "").trim(),
    city: String(next.city || prev.city || "").trim(),
    campus: String(next.campus || prev.campus || prev.city || "").trim(),
    country: String(next.country || prev.country || "").trim(),
    description: String(next.description || prev.description || "").trim(),
    address: String(next.address || prev.address || "").trim(),
  };
}

/**
 * Build "To" lines for commission invoice print/preview (respects per-invoice toggles).
 */
export function buildCommissionInvoiceBillToLines(
  clientProfile = {},
  printOptions = DEFAULT_COMMISSION_BILL_TO_PRINT_OPTIONS,
) {
  const options = normalizeCommissionBillToPrintOptions(printOptions);
  const lines = [];

  const name = String(clientProfile?.fullName || "").trim();
  const showName = options.showName !== false;
  if (showName && name) lines.push(name);

  const email = String(clientProfile?.email || "").trim();
  if (options.showEmail) {
    lines.push(`Email: ${email || "—"}`);
  }

  const phone = String(clientProfile?.phone || "").trim();
  if (options.showPhone) {
    lines.push(`Phone: ${phone || "—"}`);
  }

  const country = String(clientProfile?.country || "").trim();
  if (options.showCountry && country) {
    lines.push(`Country: ${country}`);
  }

  const campus = String(
    clientProfile?.campus || clientProfile?.city || "",
  ).trim();
  if (options.showCampus && campus) {
    lines.push(`Campus: ${campus}`);
  }

  if (options.showBillingAddress) {
    const billingAddress = String(clientProfile?.address || "").trim();
    if (billingAddress) {
      lines.push(billingAddress);
    }
  }

  return lines;
}

/**
 * "To" party fields for commission preview/PDF (application party block shape).
 */
export function buildCommissionInvoicePartyFields(
  clientProfile = {},
  printOptions = DEFAULT_COMMISSION_BILL_TO_PRINT_OPTIONS,
) {
  const options = normalizeCommissionBillToPrintOptions(printOptions);
  const profile = clientProfile || {};

  const fields = [];
  const showName = options.showName !== false;
  if (showName) {
    fields.push({
      label: "Name",
      value: String(profile.fullName || "").trim() || "—",
    });
  }

  if (options.showEmail) {
    fields.push({
      label: "Email",
      value: String(profile.email || "").trim() || "—",
    });
  }

  if (options.showPhone) {
    fields.push({
      label: "Phone",
      value: String(profile.phone || "").trim() || "—",
    });
  }

  const country = String(profile.country || "").trim();
  if (options.showCountry && country) {
    fields.push({ label: "Country", value: country });
  }

  const campus = String(profile.campus || profile.city || "").trim();
  if (options.showCampus && campus) {
    fields.push({ label: "Campus", value: campus });
  }

  if (options.showBillingAddress) {
    const billingAddress = String(profile.address || "").trim();
    fields.push({
      label: "Address",
      value: billingAddress || "—",
    });
  }

  return fields;
}

export function mergeBillToPrintOptionsIntoSnapshot(snapshot, printOptions) {
  const base =
    snapshot && typeof snapshot === "object" ? { ...snapshot } : {};
  return {
    ...base,
    bill_to_print_options: normalizeCommissionBillToPrintOptions(printOptions),
  };
}
